import React, { FC, useRef, useState } from "react";
import { IconButton, Popover } from "@mui/material";
import { Close } from "@mui/icons-material";
import { FieldValues, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { UserRole } from "@green0meter/api-client";

import { StyledActionsContainer } from "./Actions.styled";
import PopoverButton from "./components/PopoverButton";

import {
  CustomFormSectionDto,
  useMultistepFormStore,
} from "@components/common/MultistepForm";
import { Box, Button, Typography } from "@components/base";
import { useUserStore } from "@store/user";

interface ActionsProps {}

const Actions: FC<ActionsProps> = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const submitRef = useRef<HTMLButtonElement | null>(null);
  const {
    sections,
    formSettings: {
      button,
      formBasics: { isLocked },
    },
    isSubmissionPending,
  } = useMultistepFormStore();
  const { getValues } = useFormContext();
  const { t } = useTranslation();

  const { user } = useUserStore();

  const isRespondent = user?.role === UserRole.Respondent;
  const isAuditor = user?.role === UserRole.Auditor;

  const submitForm = (): void => {
    submitRef?.current?.click();
  };

  const handleSubmit = (
    feedback?: (values: FieldValues, sections: CustomFormSectionDto[]) => void,
    disableSubmit?: boolean,
  ): void => {
    if (!disableSubmit) {
      submitForm();
    }
    feedback?.(getValues(), sections);
    handleClosePopover();
  };

  const handleOpenPopover = (event: React.MouseEvent<HTMLButtonElement>): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = (): void => {
    setAnchorEl(null);
  };

  return (
    button && (
      <StyledActionsContainer>
        <Button
          fullWidth
          onClick={
            button.popoverButtons
              ? handleOpenPopover
              : (): void => handleSubmit(button.onClick, button.disableSubmit)
          }
          disabled={
            isLocked || isSubmissionPending || anchorEl !== null || isRespondent || isAuditor
          }
          isLoading={isSubmissionPending}
        >
          {button.label}
        </Button>
        <button type="submit" ref={submitRef} style={{ display: "none" }}></button>
        <Popover
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClosePopover}
          transformOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            p={2}
          >
            <Typography fontWeight={500}>
              {t("stepperActions.popoverTitle")}
            </Typography>
            <IconButton size="medium" onClick={handleClosePopover}>
              <Close />
            </IconButton>
          </Box>
          <Box display="flex" flexDirection="column" gap={2} p={2} pt={0}>
            {button.popoverButtons?.map((item, key) => (
              <PopoverButton
                title={item.title}
                description={item.description}
                onClick={() => handleSubmit(item.onClick, item.disableSubmit)}
                disabled={isLocked}
                key={`popover-button-${key}`}
              />
            ))}
          </Box>
        </Popover>
      </StyledActionsContainer>
    )
  );
};

export default Actions;
